import { Chip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import RowActions, { type RowAction } from '../../../../components/RowActions';
import ExpandableChipList from '../../../../components/ExpandableChipList';
import { resolveNamesByIds } from '../../../../utils/resolveNamesByIds';
import type { SettingsCategoryRow } from '../../types';
import type { MachineOption } from '../../../planning/types';
import { CATEGORY_ROW_LABELS as LABELS } from '../../../../utils/labels';

interface CategoryRowProps {
  category: SettingsCategoryRow;
  options: MachineOption[];
  showDivider: boolean;
  onEdit?: () => void;
  onArchive?: () => void;
  onUnarchive?: () => void;
  onDelete?: () => void;
}

export default function CategoryRow({
  category,
  options,
  showDivider,
  onEdit,
  onArchive,
  onUnarchive,
  onDelete,
}: CategoryRowProps) {
  const linkedOptionNames = resolveNamesByIds(category.option_ids ?? [], options);
  const actions: RowAction[] = category.archived
    ? onUnarchive
      ? [{ label: LABELS.unarchive, icon: <UnarchiveOutlinedIcon fontSize="small" />, onClick: onUnarchive, color: 'success', testId: 'unarchive-button' }]
      : []
    : [
        ...(onEdit ? [{ label: LABELS.edit, icon: <EditIcon fontSize="small" />, onClick: onEdit, color: 'primary' as const, testId: 'edit-button' }] : []),
        ...(category.has_machines
          ? onArchive
            ? [{ label: LABELS.archive, icon: <ArchiveOutlinedIcon fontSize="small" />, onClick: onArchive, color: 'warning' as const, testId: 'archive-button' }]
            : []
          : onDelete
            ? [{ label: LABELS.delete, icon: <DeleteOutlineIcon fontSize="small" />, onClick: onDelete, color: 'error' as const, testId: 'delete-button' }]
            : []),
      ];

  return (
    <div>
      {showDivider && <hr className="border-border-subtle" />}
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="flex-1 min-w-0">
          <p className={`font-medium mb-0 mt-0 truncate ${category.archived ? 'text-text-secondary' : ''}`}>
            {category.name}
          </p>
          {linkedOptionNames.length > 0 ? (
            <ExpandableChipList
              items={linkedOptionNames}
              renderChip={(name) => <Chip key={name} label={name} size="small" variant="outlined" />}
              showMoreLabel={LABELS.showMoreOptions}
              showLessLabel={LABELS.showLessOptions}
              toggleTestId="toggle-options-button"
            />
          ) : (
            <p className="text-sm text-text-secondary mt-1.5 mb-0">{LABELS.noOptionsLinked}</p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <RowActions actions={actions} />
        </div>
      </div>
    </div>
  );
}
